import {chainWalk} from "../util/Util";
import {CORE_CYCLE_PREFIX, EMPTY_STRING_PLACEHOLDER, DELIMITER, SETTINGS_TYPE} from "./Constants";

/**
 * This method returns the time window string for a given time window object
 * @returns string
 */
export const getWindowString = (window) => {
    let st_hr  = chainWalk(() => window.startTime.hour.toString().padStart(2, '0'), EMPTY_STRING_PLACEHOLDER)
    let st_min = chainWalk(() => window.startTime.minute.toString().padStart(2, '0'), EMPTY_STRING_PLACEHOLDER)
    let en_hr  = chainWalk(() => window.endTime.hour.toString().padStart(2, '0'), EMPTY_STRING_PLACEHOLDER)
    let en_min = chainWalk(() => window.endTime.minute.toString().padStart(2, '0'), EMPTY_STRING_PLACEHOLDER)
    return `${st_hr}:${st_min}-${en_hr}:${en_min}`
}

/**
 * Returns a list of cycle names in the following order:
 * First, a lexicographically ordered list of core cycle names
 * Followed by a lexicographically ordered list other cycle names
 * @returns string[]
 */
export const orderCycles = (cycleNames) => {
    const core_cycles = []
    const other_cycles = []
    cycleNames.forEach((name) => {
        if (name.startsWith(CORE_CYCLE_PREFIX)) {
            core_cycles.push(name)
        } else {
            other_cycles.push(name)
        }
    })
    core_cycles.sort()
    other_cycles.sort()
    return core_cycles.concat(other_cycles);
}

/**
 * Builds the settings name by combining cycle, stationCode, and settingsType with a delimiter.
 * @param {string} cycle - The cycle name
 * @param {string} stationCode - The station code
 * @returns {string} The formatted settings name
 */
export const buildSettingsName = (cycle, stationCode) => cycle + DELIMITER + stationCode + DELIMITER + SETTINGS_TYPE;

/**
 * Normalizes the raw force-induct restricted-status remote config (AppConfig/g2s2/SDC) into the
 * set of statuses that must be HIDDEN for a given station + cycle combination.
 *
 * The remote config is owned by the service, so this is tolerant of the common shapes it may take:
 *
 *  1. Station -> array of cycles for which ENABLED is restricted (default restricted status):
 *       { "DAU1": ["CYCLE_1", "AD_HOC_1"] }
 *
 *  2. Station -> cycle -> array of restricted statuses:
 *       { "DAU1": { "CYCLE_1": ["ENABLED"], "AD_HOC_1": ["ENABLED", "AUTOMATED"] } }
 *
 * Any other / missing shape yields an empty set (no restrictions).
 *
 * @param {object} config raw remote config payload keyed by station code
 * @param {string} stationCode current station code
 * @param {string} cycleName currently selected cycle name
 * @param {string} defaultRestrictedStatus status to restrict when the config only lists cycles
 * @returns {string[]} statuses to hide for this station + cycle
 */
export const getRestrictedForceInductStatuses = (config, stationCode, cycleName, defaultRestrictedStatus) => {
    const stationConfig = chainWalk(() => config[stationCode], null)
    if (!stationConfig || !cycleName) {
        return []
    }
    // Shape 1: station -> [cycles] (restrict the default status for those cycles)
    if (Array.isArray(stationConfig)) {
        return stationConfig.includes(cycleName) ? [defaultRestrictedStatus] : []
    }
    // Shape 2: station -> { cycle -> [statuses] }
    const cycleStatuses = chainWalk(() => stationConfig[cycleName], null)
    if (Array.isArray(cycleStatuses)) {
        return cycleStatuses
    }
    return []
}

/**
 * Returns the force-induct statuses that should be shown in the dropdown for the given
 * station + cycle, filtering out any statuses restricted by remote config.
 *
 * @param {object} forceStatus the FORCE_STATUS enum (key -> value)
 * @param {string[]} restrictedStatuses statuses to hide
 * @returns {string[]} allowed FORCE_STATUS keys
 */
export const getAllowedForceInductStatusKeys = (forceStatus, restrictedStatuses) => {
    const restricted = restrictedStatuses || []
    return Object.keys(forceStatus).filter(key => !restricted.includes(forceStatus[key]))
}



// WEBPACK FOOTER //
// ./src/components/inductmanagementv2/Utils.js